import {StickMaterial} from "./StickMaterial";
import {MdStickJoint} from "../../model/frame/MdFrameBlock";
import {inches} from "../../const";

// alignment slop when matching stick positions.
const WIGGLE = inches / 16;

// Geometric detection of interior joints from the laid-out sticks. Positions
// aren't in the model, so this runs after layout and produces, per stick:
//   - crossLines: cross lines where the stick is the *through* stick (perpendicular
//     sticks cap into it from BOTH sides, aligned). Flipping it transposes its
//     parent block.
//   - isTee: the stick's end butts into a perpendicular run mid-span (one side
//     only) — a forced cap.
export function detectJoints(sticks: StickMaterial[]): Map<string, MdStickJoint> {
    const geos = sticks.map(geo);
    const result = new Map<string, MdStickJoint>();
    const ensure = (id: string) => {
        let j = result.get(id);
        if(!j) { j = {crossLines: [], isTee: false}; result.set(id, j); }
        return j;
    };
    geos.forEach(g => ensure(g.id));

    for(const through of geos) {
        // group the perpendicular sticks that abut `through`'s interior by position
        // along its length; a position with sticks on both sides is a cross, one
        // side only is a tee.
        const groups: {pos: number; a: Geo[]; b: Geo[]}[] = [];
        for(const other of geos) {
            if(other.id === through.id) continue;
            const hit = abutment(through, other);
            if(!hit) continue;

            let grp = groups.find(g => Math.abs(g.pos - hit.pos) < WIGGLE);
            if(!grp) { grp = {pos: hit.pos, a: [], b: []}; groups.push(grp); }
            (hit.side === "a" ? grp.a : grp.b).push(other);
        }

        for(const grp of groups) {
            if(grp.a.length > 0 && grp.b.length > 0) {
                // a cross: `through` is the extended stick. Each capping stick meets
                // `through` at one of its ends — the side-a stick (lower coord) at its
                // B end, the side-b stick at its A end — and extending that end flips
                // the cross.
                const a0 = grp.a[0];
                const b0 = grp.b[0];
                ensure(through.id).crossLines.push({idA: a0.id, idB: b0.id});
                ensure(a0.id).extendB = through.id;
                ensure(b0.id).extendA = through.id;
            } else {
                for(const p of [...grp.a, ...grp.b]) ensure(p.id).isTee = true;
            }
        }
    }

    return result;
}

// a stick reduced to along-axis (the axis it spans) and cross-axis (its thickness)
// extents in scene coords. The cross extents are the slot the layout gave the stick;
// a sloped stick's band leaves it (see StickMaterial.facesAt).
export type Geo = {
    id: string;
    orientation: "vertical" | "horizontal";
    alongMin: number;
    alongMax: number;
    crossMin: number;
    crossMax: number;
    crossCenter: number;
};

export function geo(s: StickMaterial): Geo {
    const len = s.length.get();
    const t = s.crossThickness();
    if(s.orientation === "vertical") {
        return {
            id: s.stickId, orientation: "vertical",
            alongMin: s.positionY, alongMax: s.positionY + len,
            crossMin: s.positionX, crossMax: s.positionX + t, crossCenter: s.positionX + t / 2,
        };
    }
    return {
        id: s.stickId, orientation: "horizontal",
        alongMin: s.positionX, alongMax: s.positionX + len,
        crossMin: s.positionY, crossMax: s.positionY + t, crossCenter: s.positionY + t / 2,
    };
}

// does `other` (perpendicular) butt into `through`'s interior? Returns the position
// along `through`'s length and which side of `through` it's on. `other`'s
// cross-center sits along `through`'s length axis; its along-span must reach
// `through`'s thickness band, and the contact must be strictly interior (not at an
// end — that would be `through` capping into `other`, handled the other way round).
function abutment(through: Geo, other: Geo): {pos: number; side: "a" | "b"} | null {
    if(other.orientation === through.orientation) return null;

    const pos = other.crossCenter;
    if(!(pos > through.alongMin + WIGGLE && pos < through.alongMax - WIGGLE)) return null;

    if(other.alongMax < through.crossMin - WIGGLE) return null;
    if(other.alongMin > through.crossMax + WIGGLE) return null;

    const otherCenter = (other.alongMin + other.alongMax) / 2;
    return {pos, side: otherCenter < through.crossCenter ? "a" : "b"};
}
